import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Paper,
    Stack,
    Chip,
    CardHeader,
    Collapse,
    IconButton,
    CardActions,
    Divider,
    Skeleton,
    Alert,
    LinearProgress,
    Backdrop,
    CircularProgress,
    Fab,
    Zoom,
    Tooltip
} from '@mui/material';
import {
    ArrowBack,
    PictureAsPdf as PdfIcon,
    MenuBook as BookIcon,
    School as EducationIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    OpenInNew as OpenInNewIcon,
    GetApp as DownloadIcon,
    Fullscreen as FullscreenIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Calculate as CalculateIcon
} from '@mui/icons-material';
import ParlayCalculator from '../ParlayCalculator';

const ResourcesScreen = () => {
    const navigate = useNavigate();
    const [expandedCards, setExpandedCards] = useState({});
    const [loadingPdfs, setLoadingPdfs] = useState({});
    const [pdfErrors, setPdfErrors] = useState({});
    const [fullscreenPdf, setFullscreenPdf] = useState(null);

    // Mobile detection
    const isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            window.innerWidth <= 768;
    };

    const [isMobileDevice, setIsMobileDevice] = useState(isMobile());

    useEffect(() => {
        window.scrollTo(0, 0);

        // Update mobile detection on resize
        const handleResize = () => {
            setIsMobileDevice(isMobile());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const resources = [
        {
            id: 'betting-101',
            title: 'Sports Betting 101 - Detailed Guide',
            description: 'Comprehensive guide covering all the fundamentals of sports betting, from basic concepts to advanced strategies.',
            path: '/assets/docs/Bettor_Advantage_Sports_Betting_101_Detailed.pdf',
            category: 'Beginner Guide',
            color: 'primary'
        },
        {
            id: 'betting-terms',
            title: 'Sports Betting Terms - Full Glossary',
            description: 'Complete glossary of sports betting terminology, definitions, and key concepts every bettor should know.',
            path: '/assets/docs/Bettor_Advantage_Sports_Betting_Terms_FULL.pdf',
            category: 'Reference',
            color: 'secondary'
        }
    ];

    const handleResourceOpen = (path) => {
        window.open(path, '_blank', 'noopener,noreferrer');
    };

    const handleExpandClick = (resourceId) => {
        // Prevent expansion on mobile devices - redirect to open in new tab instead
        if (isMobileDevice) {
            const resource = resources.find(r => r.id === resourceId);
            if (resource) {
                handleResourceOpen(resource.path);
            }
            return;
        }

        setExpandedCards(prev => ({
            ...prev,
            [resourceId]: !prev[resourceId]
        }));

        if (!expandedCards[resourceId]) {
            setLoadingPdfs(prev => ({ ...prev, [resourceId]: true }));
        }
    };

    const handlePdfLoad = (resourceId) => {
        setLoadingPdfs(prev => ({ ...prev, [resourceId]: false }));
        setPdfErrors(prev => ({ ...prev, [resourceId]: false }));
    };

    const handlePdfError = (resourceId) => {
        setLoadingPdfs(prev => ({ ...prev, [resourceId]: false }));
        setPdfErrors(prev => ({ ...prev, [resourceId]: true }));
    };

    const handleFullscreen = (resource) => {
        setFullscreenPdf(resource);
    };

    const handleCloseFullscreen = () => {
        setFullscreenPdf(null);
    };

    const handleDownload = (resource) => {
        const link = document.createElement('a');
        link.href = resource.path;
        link.download = resource.title.replace(/\s+/g, '_') + '.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                backgroundColor: 'background.default',
                minHeight: '100vh',
                p: { xs: 1, sm: 2 },
                pb: { xs: 10, sm: 12 }
            }}
        >
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/')}
                >
                    Back to Home
                </Button>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    mb: 4,
                    p: { xs: 3, sm: 4 },
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    borderRadius: 2,
                    textAlign: 'center'
                }}
            >
                <EducationIcon sx={{ fontSize: 48, mb: 2, color: 'white' }} />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, color: 'white' }}>
                    Learning Resources
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto', color: 'white' }}>
                    Access comprehensive guides and educational materials to enhance your sports betting knowledge
                </Typography>
            </Paper>

            {/* Resources Grid */}
            <Grid container spacing={3}>
                {resources.map((resource) => (
                    <Grid item xs={12} key={resource.id}>
                        <Card
                            elevation={2}
                            sx={{
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            {/* Card Header */}
                            <CardHeader
                                avatar={<PdfIcon sx={{ fontSize: 32, color: 'error.main' }} />}
                                title={
                                    <Box>
                                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {resource.title}
                                        </Typography>
                                        <Chip
                                            label={resource.category}
                                            color={resource.color}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                }
                                action={
                                    <IconButton
                                        onClick={() => handleExpandClick(resource.id)}
                                        aria-expanded={expandedCards[resource.id]}
                                        aria-label="show more"
                                    >
                                        {expandedCards[resource.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                }
                                sx={{ pb: 1 }}
                            />

                            <CardContent sx={{ pt: 0 }}>
                                {/* Description */}
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2, lineHeight: 1.6 }}
                                >
                                    {resource.description}
                                </Typography>


                            </CardContent>

                            <Divider />

                            {/* Action Buttons */}
                            <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isMobileDevice ? <OpenInNewIcon /> : (expandedCards[resource.id] ? <ExpandLessIcon /> : <ViewIcon />)}
                                        onClick={() => handleExpandClick(resource.id)}
                                        sx={{
                                            bgcolor: 'primary.main',
                                            '&:hover': {
                                                bgcolor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        {isMobileDevice ? 'Open PDF' : (expandedCards[resource.id] ? 'Hide' : 'View')}
                                    </Button>
                                    {!isMobileDevice && (
                                        <Tooltip title="Open in new tab">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleResourceOpen(resource.path)}
                                            >
                                                <OpenInNewIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    {!isMobileDevice && expandedCards[resource.id] && (
                                        <Tooltip title="Fullscreen view">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleFullscreen(resource)}
                                            >
                                                <FullscreenIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Download PDF">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDownload(resource)}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </CardActions>

                            {/* Embedded PDF - Only show on desktop */}
                            <Collapse in={!isMobileDevice && expandedCards[resource.id]} timeout="auto" unmountOnExit>
                                <Box sx={{ position: 'relative' }}>
                                    {loadingPdfs[resource.id] && (
                                        <Box sx={{ px: 2, pb: 2 }}>
                                            <LinearProgress sx={{ mb: 1 }} />
                                            <Typography variant="body2" color="text.secondary" align="center">
                                                Loading PDF...
                                            </Typography>
                                        </Box>
                                    )}

                                    {pdfErrors[resource.id] ? (
                                        <Box sx={{ p: 2 }}>
                                            <Alert
                                                severity="error"
                                                action={
                                                    <Button
                                                        color="inherit"
                                                        size="small"
                                                        startIcon={<RefreshIcon />}
                                                        onClick={() => {
                                                            setPdfErrors(prev => ({ ...prev, [resource.id]: false }));
                                                            setLoadingPdfs(prev => ({ ...prev, [resource.id]: true }));
                                                        }}
                                                    >
                                                        Retry
                                                    </Button>
                                                }
                                            >
                                                Failed to load PDF. Click retry or use the "Open in New Tab" option.
                                            </Alert>
                                        </Box>
                                    ) : (
                                        <CardContent sx={{ pt: 0, pb: 0 }}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    width: '100%',
                                                    height: { xs: '400px', sm: '600px', md: '800px' },
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    bgcolor: 'grey.50'
                                                }}
                                            >
                                                <Box
                                                    component="iframe"
                                                    src={`${resource.path}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        borderRadius: 1,
                                                        display: loadingPdfs[resource.id] ? 'none' : 'block'
                                                    }}
                                                    title={resource.title}
                                                    onLoad={() => handlePdfLoad(resource.id)}
                                                    onError={() => handlePdfError(resource.id)}
                                                />

                                                {/* Loading skeleton */}
                                                {loadingPdfs[resource.id] && (
                                                    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
                                                        <Skeleton variant="rectangular" width="100%" sx={{ flexGrow: 1 }} />
                                                    </Box>
                                                )}
                                            </Paper>
                                        </CardContent>
                                    )}
                                </Box>
                            </Collapse>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Betting Tools Section Header */}
            <Paper
                elevation={0}
                sx={{
                    mt: 4,
                    mb: 3,
                    p: { xs: 2, sm: 3 },
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'white',
                    borderRadius: 2,
                    textAlign: 'center'
                }}
            >
                <CalculateIcon sx={{ fontSize: 40, mb: 1, color: 'white' }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'white' }}>
                    Betting Tools
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                    Free calculators to help you make smarter betting decisions
                </Typography>
            </Paper>

            {/* Parlay Calculator */}
            <Box sx={{ mb: 4 }}>
                <ParlayCalculator />
            </Box>

            {/* Additional Info Section */}
            <Paper
                elevation={1}
                sx={{
                    mt: 4,
                    p: 3,
                    backgroundColor: 'background.paper',
                    borderRadius: 2
                }}
            >
                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    About These Resources
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    These educational materials have been carefully curated to provide you with essential knowledge
                    about sports betting. Whether you're a beginner looking to understand the basics or an experienced
                    bettor seeking to expand your vocabulary, these resources will help you make more informed decisions
                    and improve your betting strategy.
                </Typography>
            </Paper>

            {/* Fullscreen PDF Modal */}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={!!fullscreenPdf}
                onClick={handleCloseFullscreen}
            >
                {fullscreenPdf && (
                    <Box
                        sx={{
                            width: '95vw',
                            height: '95vh',
                            position: 'relative',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Fullscreen Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'primary.main',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {fullscreenPdf.title}
                            </Typography>
                            <IconButton
                                onClick={handleCloseFullscreen}
                                sx={{ color: 'white' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Fullscreen PDF */}
                        <Box
                            component="iframe"
                            src={`${fullscreenPdf.path}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                            sx={{
                                width: '100%',
                                height: 'calc(100% - 64px)',
                                border: 'none'
                            }}
                            title={fullscreenPdf.title}
                        />
                    </Box>
                )}
            </Backdrop>

            {/* Floating Action Button for quick access - Desktop only */}
            <Zoom in={!isMobileDevice && Object.values(expandedCards).some(Boolean)}>
                <Fab
                    color="primary"
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 80, sm: 24 },
                        right: 24,
                        zIndex: 1000
                    }}
                    onClick={() => {
                        const firstExpanded = Object.keys(expandedCards).find(key => expandedCards[key]);
                        if (firstExpanded) {
                            const resource = resources.find(r => r.id === firstExpanded);
                            if (resource) handleFullscreen(resource);
                        }
                    }}
                >
                    <FullscreenIcon />
                </Fab>
            </Zoom>
        </Box>
    );
};

export default ResourcesScreen;